import { watch, type Directive, type WatchStopHandle } from 'vue'

import { useAdminApplicationStore } from '@/stores/admin/application'
import {
  hasAdminPermission,
  type AdminPermissionRequirement
} from '@/apps/admin/utils/permissions'

const cleanupByElement = new WeakMap<HTMLElement, WatchStopHandle>()

const applyPermissionVisibility = (
  element: HTMLElement,
  grantedPermissions: readonly string[],
  requirement: AdminPermissionRequirement
): void => {
  const allowed = hasAdminPermission(grantedPermissions, requirement)
  element.hidden = !allowed

  if (allowed) {
    element.removeAttribute('aria-hidden')
  } else {
    element.setAttribute('aria-hidden', 'true')
  }
}

const bindPermission = (
  element: HTMLElement,
  requirement: AdminPermissionRequirement
): void => {
  cleanupByElement.get(element)?.()

  const applicationStore = useAdminApplicationStore()
  cleanupByElement.set(element, watch(
    () => applicationStore.permissions,
    permissions => applyPermissionVisibility(element, permissions, requirement),
    { deep: true, immediate: true }
  ))
}

/**
 * v-admin-permission：隐藏当前账号无权操作的元素，并在权限初始化/刷新后同步更新。
 * 该指令不替代服务端权限校验。
 */
export const adminPermissionDirective: Directive<HTMLElement, AdminPermissionRequirement> = {
  mounted(element, binding) {
    bindPermission(element, binding.value)
  },
  updated(element, binding) {
    if (binding.value !== binding.oldValue) {
      bindPermission(element, binding.value)
    }
  },
  unmounted(element) {
    cleanupByElement.get(element)?.()
    cleanupByElement.delete(element)
  }
}
